import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'AutomaticEmbeddings' });

export const useAutomaticEmbeddings = () => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const runAutomaticBackfill = async () => {
      // Only run for admin users
      if (!user) return;

      try {
        // Check if user is admin using new role system
        const { data: adminRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .or('expires_at.is.null,expires_at.gt.now()')
          .maybeSingle();

        if (!adminRole) return;

        // Check if we've already run this successfully (stored in localStorage)
        const lastBackfill = localStorage.getItem('embeddings_backfill_completed');
        if (lastBackfill) {
          const lastTime = new Date(lastBackfill);
          const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
          if (lastTime > oneDayAgo) {
            log.info('Embeddings backfill already completed recently, skipping');
            return;
          }
        }

        log.info('Checking if embeddings backfill is needed...');
        setIsProcessing(true);

        // Check if there are profiles with bios that need embeddings
        const { data: profilesNeedingEmbeddings } = await supabase
          .from('profiles')
          .select('id')
          .not('bio', 'is', null)
          .neq('bio', '')
          .limit(5);

        if (!profilesNeedingEmbeddings || profilesNeedingEmbeddings.length === 0) {
          log.info('No profiles need embeddings backfill');
          localStorage.setItem('embeddings_backfill_completed', new Date().toISOString());
          return;
        }

        log.info(`Found ${profilesNeedingEmbeddings.length} profiles that need embeddings, starting backfill...`);
        
        // Check if functions API is available
        if (!supabase?.functions || typeof supabase.functions.invoke !== 'function') {
          log.warn('Supabase Functions API not available - skipping automatic embeddings backfill');
          return;
        }
        
        // Trigger the backfill
        const { data, error } = await supabase.functions.invoke('backfill-profile-embeddings');
        
        if (error) {
          log.warn('Automatic backfill failed (functions may not be running):', error.message);
        } else {
          log.info('Automatic backfill completed:', data);
          localStorage.setItem('embeddings_backfill_completed', new Date().toISOString());
        }

      } catch (error) {
        log.error('Error in automatic embeddings backfill:', error);
      } finally {
        setIsProcessing(false);
      }
    };

    // Run on mount with a small delay
    const timer = setTimeout(runAutomaticBackfill, 2000);
    return () => clearTimeout(timer);
  }, [user]);

  return {
    isProcessing
  };
};